/**
 * Firebase Analytics Utility
 * 
 * This file contains helper functions to track events with Firebase Analytics.
 * Firebase Analytics automatically tracks page_view events, but we track custom events here.
 */

import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase';

/**
 * Helper to safely log events
 * @param {string} eventName - Firebase Analytics event name
 * @param {object} eventParams - Event parameters
 */
const trackEvent = (eventName, eventParams = {}) => {
  if (analytics) {
    try {
      logEvent(analytics, eventName, {
        ...eventParams,
        timestamp: new Date().toISOString()
      });
      console.log(`🔥 Analytics: ${eventName}`, eventParams);
    } catch (error) {
      console.warn('⚠️ Analytics error:', error);
    }
  } else {
    console.warn('⚠️ Analytics not initialized');
  }
};

/**
 * Track when user performs initial search (pre-search)
 * @param {string} selectorType - 'email' or 'phone'
 * @param {number} resultsCount - Number of results found
 */
export const trackSearch = (selectorType, resultsCount) => {
  trackEvent('search', {
    search_term: selectorType,
    results_count: resultsCount
  });
};

/**
 * Track when user selects a tier (begins checkout)
 * Uses Firebase's recommended 'select_item' event
 * @param {string} tier - 'basic' or 'deepdive'
 * @param {string} selectorType - 'email' or 'phone'
 * @param {number} resultsCount - Number of pre-search results
 */
export const trackTierSelected = (tier, selectorType, resultsCount) => {
  trackEvent('select_item', {
    item_list_id: 'pricing_tiers',
    item_list_name: 'Pricing Tiers',
    items: [{
      item_id: tier,
      item_name: tier === 'basic' ? 'Basic Check' : 'Deep Dive',
      item_category: 'report_tier',
      price: tier === 'basic' ? 1.00 : 5.00
    }],
    selector_type: selectorType,
    results_count: resultsCount
  });
};

/**
 * Track when user is redirected to Stripe checkout
 * Uses Firebase's recommended 'begin_checkout' event
 * @param {string} reportId - The report ID
 * @param {string} tier - 'tier1' or 'tier2'
 * @param {string} selectorType - 'email' or 'phone'
 */
export const trackCheckoutInitiated = (reportId, tier, selectorType) => {
  const value = tier === 'tier1' ? 1.00 : 5.00;
  
  trackEvent('begin_checkout', {
    currency: 'USD',
    value: value,
    items: [{
      item_id: tier,
      item_name: tier === 'tier1' ? 'Basic Check' : 'Deep Dive Report',
      item_category: 'report',
      price: value,
      quantity: 1
    }],
    report_id: reportId,
    selector_type: selectorType
  });
};

/**
 * Track successful payment
 * Uses Firebase's recommended 'purchase' event (ecommerce)
 * @param {string} reportId - The report ID
 * @param {string} tier - 'tier1' or 'tier2'
 * @param {string} selectorType - 'email' or 'phone'
 * @param {number} amount - Amount paid in cents
 */
export const trackPurchase = (reportId, tier, selectorType, amount) => {
  const value = amount / 100; // Convert cents to dollars
  
  trackEvent('purchase', {
    transaction_id: reportId,
    value: value,
    currency: 'USD',
    items: [{
      item_id: tier,
      item_name: tier === 'tier1' ? 'Basic Check' : 'Deep Dive Report',
      item_category: 'report',
      price: value,
      quantity: 1
    }],
    selector_type: selectorType
  });
};

/**
 * Track payment cancellation
 * @param {string} selectorType - 'email' or 'phone'
 */
export const trackPaymentCancelled = (selectorType) => {
  trackEvent('checkout_abandoned', {
    selector_type: selectorType,
    stage: 'payment'
  });
};

/**
 * Track when user views their report
 * @param {string} reportId - The report ID
 * @param {string} tier - 'tier1' or 'tier2'
 * @param {number} resultsCount - Number of results in report
 */
export const trackReportViewed = (reportId, tier, resultsCount) => {
  trackEvent('view_item', {
    item_id: reportId,
    item_name: `Report - ${tier}`,
    item_category: 'report',
    tier: tier,
    results_count: resultsCount
  });
};

/**
 * Track when user requests an upgrade
 * @param {string} reportId - The report ID
 * @param {string} fromTier - Current tier
 * @param {string} toTier - Target tier
 */
export const trackUpgradeInitiated = (reportId, fromTier, toTier) => {
  trackEvent('upgrade_initiated', {
    report_id: reportId,
    from_tier: fromTier,
    to_tier: toTier
  });
};

/**
 * Track errors
 * @param {string} errorType - Type of error
 * @param {string} errorMessage - Error message
 * @param {string} location - Where the error occurred
 */
export const trackError = (errorType, errorMessage, location) => {
  trackEvent('exception', {
    description: `${errorType}: ${errorMessage}`,
    fatal: false,
    error_location: location
  });
};

/**
 * Track user engagement time on report page
 * @param {number} secondsSpent - Time spent viewing report
 * @param {string} reportId - The report ID
 */
export const trackEngagement = (secondsSpent, reportId) => {
  trackEvent('user_engagement', {
    engagement_time_msec: secondsSpent * 1000,
    report_id: reportId
  });
};

/**
 * Track when user shares results (if you add sharing)
 * @param {string} method - Share method (e.g., 'copy_link', 'email')
 * @param {string} reportId - The report ID
 */
export const trackShare = (method, reportId) => {
  trackEvent('share', {
    method: method,
    content_type: 'report',
    item_id: reportId
  });
};

export default {
  trackSearch,
  trackTierSelected,
  trackCheckoutInitiated,
  trackPurchase,
  trackPaymentCancelled,
  trackReportViewed,
  trackUpgradeInitiated,
  trackError,
  trackEngagement,
  trackShare
};

